import React, { useContext, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import StarekContext from "../../context/StarekContext";

// What a plan includes beyond its numeric ceilings. The counts are never listed
// here — they come from the backend's plan definitions, and for the workspace's
// own plan from the limits it is actually held to, so a card can't claim an
// allowance the server would refuse.
const PLAN_EXTRAS = {
    starter: {
        highlight: false,
        features: ["Role-based access control", "Email notifications"],
    },
    pro: {
        highlight: true,
        badge: "Most Popular",
        features: [
            "Everything in Starter",
            "Webhook integrations",
            "Priority support",
        ],
    },
    custom: {
        highlight: false,
        features: ["Everything in Pro", "Dedicated support", "Custom SLA"],
    },
};

const countLabel = (value, singular, plural) => {
    if (value === -1) return `Unlimited ${plural}`;
    if (value === undefined || value === null) return null;
    return `Up to ${value.toLocaleString()} ${value === 1 ? singular : plural}`;
};

const limitFeatures = (limits) => {
    if (!limits) return [];

    const devices = countLabel(limits.devices, "Device", "Devices");
    const packSuffix =
        limits.devicePackSize > 1 ? ` (packs of ${limits.devicePackSize})` : "";

    const reports = countLabel(limits.reportsPerMonth, "Report", "Reports");

    return [
        countLabel(limits.plants, "Plant", "Plants"),
        devices ? `${devices}${packSuffix}` : null,
        reports ? `${reports} / month` : null,
    ].filter(Boolean);
};

function StarekPlansModal({ show, handleClose }) {
    const { subscription, lastUsedWorkspace } = useContext(StarekContext);
    const currentPlan = subscription?.plan;

    const [plans, setPlans] = useState(null);
    const [plansError, setPlansError] = useState(null);

    const engine = lastUsedWorkspace?.engine;
    const plansUrl = engine
        ? `${engine.scheme}://${engine.host}:${engine.port}${process.env.REACT_APP_API_PREFIX}/subscription/plans`
        : null;

    useEffect(() => {
        if (!show || !plansUrl) return;

        let cancelled = false;

        setPlansError(null);
        fetch(plansUrl, { headers: { "Content-Type": "application/json" } })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.json();
            })
            .then((data) => {
                if (!cancelled) setPlans(data?.plans ?? []);
            })
            .catch((err) => {
                if (!cancelled) setPlansError(err.message || "Request failed");
            });

        return () => {
            cancelled = true;
        };
    }, [show, plansUrl]);

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-crown me-2 text-warning"></i>
                    Subscription Plans
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-muted mb-4 text-center">
                    Choose the plan that fits your operation. All plans include
                    core device monitoring, scheduling, and reporting features.
                </p>

                {plansError && (
                    <div className="alert alert-warning" role="alert">
                        Couldn't load the available plans: {plansError}
                    </div>
                )}

                {!plans && !plansError && (
                    <p className="text-muted text-center my-4">
                        Loading plans…
                    </p>
                )}

                <div className="row g-3">
                    {(plans ?? []).map((plan) => {
                        const isActive = currentPlan === plan.key;
                        const extras = PLAN_EXTRAS[plan.key] ?? { features: [] };

                        // On the workspace's own plan, show the limits it is held
                        // to. An operator can cap a pack below the plan default in
                        // the provisioning portal, so these are not always equal.
                        const limits = isActive
                            ? subscription?.limits ?? plan.limits
                            : plan.limits;

                        return (
                            <div key={plan.key} className="col-12 col-md-4 d-flex align-items-stretch">
                                <div
                                    className={`card w-100 position-relative ${
                                        extras.highlight
                                            ? "border-primary border-2 shadow"
                                            : "border"
                                    }`}
                                    style={{ borderRadius: "0.75rem", overflow: "hidden" }}
                                >
                                    {/* Popular badge */}
                                    {extras.badge && (
                                        <div
                                            className="bg-primary text-white text-center py-1"
                                            style={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.05em" }}
                                        >
                                            {extras.badge}
                                        </div>
                                    )}

                                    <div className="card-body d-flex flex-column p-4">
                                        {/* Plan name */}
                                        <div className="mb-3">
                                            <h5 className="fw-bold mb-1">
                                                {isActive
                                                    ? subscription?.planLabel ?? plan.label
                                                    : plan.label}
                                            </h5>
                                        </div>

                                        {/* Features */}
                                        <ul className="list-unstyled mb-4 flex-grow-1">
                                            {[...limitFeatures(limits), ...extras.features].map((f) => (
                                                <li key={f} className="mb-2 d-flex align-items-start gap-2">
                                                    <i className="far fa-check-circle text-success mt-1" style={{ flexShrink: 0 }}></i>
                                                    <span style={{ fontSize: "0.9rem" }}>{f}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* CTA */}
                                        <div className="mt-auto">
                                            {isActive ? (
                                                <div className="text-center">
                                                    <span className="badge bg-success px-3 py-2" style={{ fontSize: "0.85rem" }}>
                                                        <i className="fa fa-check me-1"></i>
                                                        Current Plan
                                                    </span>
                                                </div>
                                            ) : plan.key === "custom" ? (
                                                <a
                                                    href="mailto:sales@starek.com?subject=Custom Plan Enquiry"
                                                    className="btn btn-outline-primary w-100"
                                                >
                                                    Contact Sales
                                                </a>
                                            ) : (
                                                <a
                                                    href={`mailto:sales@starek.com?subject=Upgrade to Starek ${plan.label}`}
                                                    className="btn btn-primary w-100"
                                                >
                                                    Upgrade to {plan.label}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Current usage summary */}
                {subscription && (
                    <div className="mt-4 p-3 bg-light rounded border">
                        <div className="d-flex flex-wrap gap-4 align-items-center">
                            <strong className="text-muted" style={{ fontSize: "0.85rem" }}>
                                Current Usage
                            </strong>
                            <span style={{ fontSize: "0.85rem" }}>
                                <i className="fa fa-leaf text-success me-1"></i>
                                <strong>{subscription.usage?.plants ?? "—"}</strong>
                                {" / "}
                                {subscription.limits?.plants === -1 ? "∞" : subscription.limits?.plants} plants
                            </span>
                            <span style={{ fontSize: "0.85rem" }}>
                                <i className="fa fa-microchip text-primary me-1"></i>
                                <strong>{subscription.usage?.devices ?? "—"}</strong>
                                {" / "}
                                {subscription.limits?.devices === -1 ? "∞" : subscription.limits?.devices} devices
                            </span>
                            <span style={{ fontSize: "0.85rem" }}>
                                <i className="fa fa-file-alt text-secondary me-1"></i>
                                <strong>{subscription.usage?.reports ?? "—"}</strong>
                                {" / "}
                                {subscription.limits?.reportsPerMonth === -1
                                    ? "∞"
                                    : subscription.limits?.reportsPerMonth?.toLocaleString()}{" "}
                                reports / mo
                            </span>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default StarekPlansModal;
